import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

interface Display {
  id: string;
  name: string;
  description: string;
  latitude: number;
  longitude: number;
  city: string;
  state: string;
  holiday_type: string;
  display_type: string;
  image_url: string;
  featured: boolean;
  upvote_count: number;
}

interface SimpleMapViewProps {
  displays: Display[];
  userLocation?: { latitude: number; longitude: number } | null;
  selectedDisplayId?: string | null;
  onDisplaySelect?: (displayId: string | null) => void;
  selectedState?: string; // Two-letter state code (e.g., "VA", "CA")
  className?: string;
}

export const SimpleMapView: React.FC<SimpleMapViewProps> = ({
  displays,
  userLocation,
  selectedDisplayId,
  onDisplaySelect,
  selectedState,
  className = ""
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current) return;

    // Set Mapbox access token
    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: userLocation ? [userLocation.longitude, userLocation.latitude] : [-98.5795, 39.8283], // Center of US
      zoom: userLocation ? 10 : 4,
      maxZoom: 18,
      minZoom: 2
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add user location control
    map.current.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: {
          enableHighAccuracy: true
        },
        trackUserLocation: true,
        showUserHeading: true
      }),
      'top-right'
    );

    // Wait for map to load
    map.current.on('load', () => {
      setMapLoaded(true);

      // Add Mapbox state boundaries source for highlighting
      if (map.current) {
        map.current.addSource('state-boundaries', {
          type: 'vector',
          url: 'mapbox://mapbox.boundaries-adm1-v3'
        });

        // Add fill layer for state highlighting
        map.current.addLayer({
          id: 'state-fill',
          type: 'fill',
          source: 'state-boundaries',
          'source-layer': 'boundaries_admin_1',
          filter: ['==', ['get', 'iso_3166_2'], ''], // Initially hide all
          paint: {
            'fill-color': '#3b82f6',
            'fill-opacity': 0.15
          }
        }, 'clusters'); // Add before clusters layer so it doesn't cover markers

        // Add outline layer for state boundary
        map.current.addLayer({
          id: 'state-outline',
          type: 'line',
          source: 'state-boundaries',
          'source-layer': 'boundaries_admin_1',
          filter: ['==', ['get', 'iso_3166_2'], ''], // Initially hide all
          paint: {
            'line-color': '#3b82f6',
            'line-width': 3,
            'line-opacity': 0.8
          }
        }, 'clusters');
      }
    });

    // Cleanup function
    return () => {
      if (map.current) {
        map.current.remove();
      }
    };
  }, [userLocation]);

  // Update displays when data changes
  useEffect(() => {
    if (!map.current || !mapLoaded || !displays.length) return;

    // Convert displays to GeoJSON
    const geojsonData = {
      type: 'FeatureCollection' as const,
      features: displays.map(display => ({
        type: 'Feature' as const,
        geometry: {
          type: 'Point' as const,
          coordinates: [display.longitude, display.latitude]
        },
        properties: {
          id: display.id,
          name: display.name,
          description: display.description,
          city: display.city,
          state: display.state,
          holiday_type: display.holiday_type,
          display_type: display.display_type,
          image_url: display.image_url,
          featured: display.featured,
          upvote_count: display.upvote_count
        }
      }))
    };

    // Remove existing source and layers if they exist
    if (map.current.getSource('displays')) {
      if (map.current.getLayer('clusters')) map.current.removeLayer('clusters');
      if (map.current.getLayer('cluster-count')) map.current.removeLayer('cluster-count');
      if (map.current.getLayer('unclustered-point')) map.current.removeLayer('unclustered-point');
      map.current.removeSource('displays');
    }

    // Add displays source with clustering
    map.current.addSource('displays', {
      type: 'geojson',
      data: geojsonData,
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50
    });

    // Add cluster circles
    map.current.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'displays',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': [
          'step',
          ['get', 'point_count'],
          '#3b82f6', // Blue for clusters with < 10 points (better contrast on dark)
          10,
          '#f59e0b', // Amber for clusters with 10-30 points
          30,
          '#ef4444'  // Red for clusters with > 30 points
        ],
        'circle-radius': [
          'step',
          ['get', 'point_count'],
          20, // Radius for clusters with < 10 points
          10,
          30, // Radius for clusters with 10-30 points
          30,
          40  // Radius for clusters with > 30 points
        ]
      }
    });

    // Add cluster count labels
    map.current.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'displays',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12
      },
      paint: {
        'text-color': '#ffffff'
      }
    });

    // Add unclustered points
    map.current.addLayer({
      id: 'unclustered-point',
      type: 'circle',
      source: 'displays',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          '#ef4444', // Red for selected display
          ['get', 'featured'],
          '#fbbf24', // Bright gold for featured displays (better on dark)
          '#60a5fa'  // Lighter blue for regular displays (better on dark)
        ],
        'circle-radius': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          10, // Larger for selected display
          ['get', 'featured'],
          8, // Larger for featured displays
          6  // Regular size
        ],
        'circle-stroke-width': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          3, // Thicker border for selected
          2  // Regular border
        ],
        'circle-stroke-color': [
          'case',
          ['boolean', ['feature-state', 'selected'], false],
          '#ffffff', // White border for selected
          '#ffffff'  // White border for all
        ]
      }
    });

    // Handle cluster clicks (zoom to cluster)
    map.current.on('click', 'clusters', (e) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: ['clusters']
      });

      const clusterId = features[0].properties!.cluster_id;
      (map.current!.getSource('displays') as mapboxgl.GeoJSONSource).getClusterExpansionZoom(
        clusterId,
        (err, zoom) => {
          if (err) return;

          map.current!.easeTo({
            center: (features[0].geometry as GeoJSON.Point).coordinates as [number, number],
            zoom: zoom
          });
        }
      );
    });

    // Handle unclustered point clicks
    map.current.on('click', 'unclustered-point', (e) => {
      const features = e.features;
      if (!features || features.length === 0) return;

      const clickedDisplayId = features[0].properties!.id;

      // If clicking the same display, deselect it
      if (selectedDisplayId === clickedDisplayId) {
        onDisplaySelect?.(null);
        return;
      }

      // Select the new display
      onDisplaySelect?.(clickedDisplayId);
    });

    // Change cursor on hover
    map.current.on('mouseenter', 'clusters', () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'clusters', () => {
      map.current!.getCanvas().style.cursor = '';
    });

    map.current.on('mouseenter', 'unclustered-point', () => {
      map.current!.getCanvas().style.cursor = 'pointer';
    });

    map.current.on('mouseleave', 'unclustered-point', () => {
      map.current!.getCanvas().style.cursor = '';
    });

    // Handle map clicks to clear selection
    map.current.on('click', (e) => {
      // Only clear if clicking on empty map area (not on markers or clusters)
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: ['clusters', 'unclustered-point']
      });

      if (features.length === 0) {
        // Clear selection when clicking empty map area
        onDisplaySelect?.(null);
      }
    });

    // Auto-fit map to show all displays (Yelp-style behavior)
    if (displays.length > 0) {
      // Calculate bounds from all display coordinates
      const bounds = new mapboxgl.LngLatBounds();

      displays.forEach(display => {
        // Extend bounds to include this display's coordinates
        bounds.extend([display.longitude, display.latitude]);
      });

      // Fit map to bounds with padding and constraints
      try {
        map.current.fitBounds(bounds, {
          padding: { top: 50, bottom: 50, left: 50, right: 50 }, // Yelp-style padding
          maxZoom: 15, // Don't zoom in too close for single/nearby pins
          duration: 1000, // Smooth 1-second animation
        });
      } catch (error) {
        // Handle edge case where bounds might be invalid
        console.warn('Could not fit bounds:', error);
      }
    }

  }, [displays, mapLoaded, selectedDisplayId, onDisplaySelect]);

  // Manage feature state based on selectedDisplayId
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    // Clear all feature states first
    displays.forEach(display => {
      try {
        map.current!.setFeatureState(
          { source: 'displays', id: display.id },
          { selected: false }
        );
      } catch (error) {
        // Feature might not exist yet, ignore error
      }
    });

    // Set selected state for the selected display
    if (selectedDisplayId) {
      try {
        map.current!.setFeatureState(
          { source: 'displays', id: selectedDisplayId },
          { selected: true }
        );
      } catch (error) {
        // Feature might not exist yet, ignore error
      }
    }
  }, [selectedDisplayId, displays, mapLoaded]);

  // Update state boundary highlight when selectedState changes
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    try {
      if (selectedState) {
        // Show and filter to selected state using ISO 3166-2 format (e.g., US-VA)
        const isoCode = `US-${selectedState}`;

        if (map.current.getLayer('state-fill')) {
          map.current.setFilter('state-fill', ['==', ['get', 'iso_3166_2'], isoCode]);
        }
        if (map.current.getLayer('state-outline')) {
          map.current.setFilter('state-outline', ['==', ['get', 'iso_3166_2'], isoCode]);
        }
      } else {
        // Hide state boundary layers when no state is selected
        if (map.current.getLayer('state-fill')) {
          map.current.setFilter('state-fill', ['==', ['get', 'iso_3166_2'], '']);
        }
        if (map.current.getLayer('state-outline')) {
          map.current.setFilter('state-outline', ['==', ['get', 'iso_3166_2'], '']);
        }
      }
    } catch (error) {
      console.error('Error updating state boundary:', error);
    }
  }, [selectedState, mapLoaded]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <div ref={mapContainer} className="w-full h-full" />
      {!mapLoaded && (
        <div className="absolute inset-0 bg-muted flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
    </div>
  );
};